import { createAIInterpreter } from './aiInterpreter';
import { COMMAND_TOOLS } from './commandTools';
import { getConfig, validateConfig } from './config';
import toast from 'react-hot-toast';

// Automation System Exports
export {
  localNormalizeCommand,
  resolveWorkingCommand,
  buildNormalizedPrompt,
} from './promptNormalizer';
export { COMMAND_TOOLS, executeTool } from './commandTools';
export { AIInterpreter, createAIInterpreter } from './aiInterpreter';
export { AUTOMATION_CONFIG, getConfig, validateConfig } from './config';
export {
  normalizeColor,
  pickCreateProps,
  enrichToolCallsFromCommand,
  applyMeshColor,
  applyMeshOpacity,
  applyMeshVisualProps,
  sanitizeTextContent,
  inferColorFromCommand,
  inferRotationFromCommand,
  parseRotationFromText,
  inferOpacityFromCommand,
  inferMaterialPropsFromCommand,
  inferScaleFromCommand,
} from './propertyHelpers';
export {
  searchLibraryModels,
  findBestLibraryModel,
  scoreLibraryItem,
  isGenericModelQuery,
  getVarietyQueries,
} from './librarySearchService';
export {
  normalizeModelSearchQuery,
  itemMatchesSubject,
  subjectRelevanceScore,
} from './modelRelevance';
export {
  parseSpatialRelation,
  enrichSpatialToolCalls,
  enrichGroupLayoutToolCalls,
  enrichIndividuatedObjectToolCalls,
  enrichMultiTypeAddToolCalls,
  parseMultiTypeAddSpecs,
  isIndividuatedMultiAdd,
  parseIndividuatedObjectVariants,
  applyRelativePlacement,
  findAnchorObject,
  parseQuantityFromText,
  parseQuantitiesByObjectType,
  parseGroupLayout,
  computeGroupPositions,
  detectOrientationAxis,
} from './spatialLayout';
export {
  addModelFromLibraryOrSketchfab,
  collectExistingModelIds,
  wantsSameModelAgain,
} from './modelAddService';
export {
  executeAddText,
  executeAddHotspot,
  buildSceneContext,
} from './contentTools';

// Main automation class that combines everything
export class AutomationSystem {
  constructor(config = null) {
    this.config = config || getConfig();
    this.interpreter = createAIInterpreter(this.config.backend.apiUrl);

    // Validate configuration
    const errors = validateConfig(this.config);
    if (errors.length > 0) {
      console.warn('Automation configuration warnings:', errors);
    }
  }

  // Main method to process user commands
  async processCommand(userCommand, scene, transformControls, store) {
    try {
      const result = await this.interpreter.interpretAndExecute(
        userCommand,
        scene,
        transformControls,
        store
      );

      if (result.success) {
        if (
          result.normalizedCommand &&
          result.originalCommand &&
          result.normalizedCommand.toLowerCase() !==
            result.originalCommand.toLowerCase()
        ) {
          console.info(
            '[automation] used normalized command:',
            result.normalizedCommand
          );
        }
        if (result.results) {
          result.results.forEach(({ tool, result: toolResult, error }) => {
            if (error) {
              console.error(`❌ Tool ${tool} failed:`, error);
              toast.error(`Tool ${tool} failed: ${error}`);
            } else if (toolResult?.success === false) {
              console.error(`❌ Tool ${tool} failed:`, toolResult.message);
              toast.error(
                `Tool ${tool} failed: ${toolResult.message || 'Unknown error'}`
              );
            } else if (toolResult?.message) {
              toast.success(toolResult.message);
            }
          });
        }
      } else {
        console.error(`❌ Command failed:`, result.message);
        toast.error(result.message || result.error || 'Command failed');
      }

      return result;
    } catch (error) {
      console.error('🚨 Automation system error:', error);
      toast.error(error.message || 'Automation system error');
      return {
        success: false,
        error: error.message,
        fallback: true,
      };
    }
  }

  // Get available tools
  getAvailableTools() {
    return COMMAND_TOOLS;
  }

  // Update configuration
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.interpreter = createAIInterpreter(this.config.backend.apiUrl);
  }

  // Test connection to backend API
  async testConnection() {
    try {
      const token = localStorage.getItem('token') || '';
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${this.config.backend.apiUrl}/api/v1/bedrock/test`,
        {
          method: 'GET',
          headers,
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        return {
          connected: true,
          message: 'Successfully connected to backend API',
          details: data,
        };
      } else {
        return {
          connected: false,
          error: `HTTP ${response.status}`,
          message: 'Failed to connect to backend API',
        };
      }
    } catch (error) {
      return {
        connected: false,
        error: error.message,
        message: 'Failed to connect to backend API',
      };
    }
  }
}

// Default export
export default AutomationSystem;
